import { Link, useNavigate } from "react-router-dom";
import Top from "./Top";
import Footer from "./Footer";
import { useEffect, useState } from "react";

function EnrolledCourses() {
  const navigate = useNavigate();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notLoggedIn, setNotLoggedIn] = useState(false);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const userDetails = JSON.parse(
    localStorage.getItem("SLNUserDetails") || "{}",
  );
  const userId = userDetails?.userId;
  const token = localStorage.getItem("SLNToken");

  const reqHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };

  // ── Gate: not logged in ───────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !userId) {
      setNotLoggedIn(true);
      setLoading(false);
    }
  }, [token, userId]);

  // ── Fetch enrolled courses (payment history) ──────────────────────────────
  useEffect(() => {
    if (!userId || !token) return;

    const fetchPayments = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Payment/GetPaymentHistory?userId=${userId}`,
          { headers: reqHeaders },
        );
        const data = await res.json();

        // Only keep successful payments
        const allPayments = Array.isArray(data?.data) ? data.data : [];
        const successfulPayments = allPayments.filter(
          (p) =>
            p.status === "SUCCESS" ||
            p.status === "success" ||
            p.paymentStatus === "SUCCESS" ||
            p.paymentStatus === "success",
        );
        setEnrolledCourses(successfulPayments);
      } catch (err) {
        console.error("Failed to fetch payments:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [userId, token]);

  // ─────────────────────────────────────────────────────────────────────────
  // ── Gate: Not logged in ────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  if (notLoggedIn) {
    return (
      <>
        <Top />
        <section className="py-8 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 col-md-8 col-12 text-center">
                <div className="mb-5">
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "#fff3cd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <i
                      className="bi bi-lock-fill text-warning"
                      style={{ fontSize: 36 }}
                    ></i>
                  </div>
                  <h2 className="fw-bold text-dark mb-2">
                    Login to View Your Courses
                  </h2>
                  <p className="text-secondary fs-5 lh-sm">
                    Your enrolled courses are waiting for you. Please log in to
                    access them.
                  </p>
                </div>
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                  <Link
                    to="/login"
                    className="btn btn-primary px-6 rounded-pill"
                  >
                    <i className="bi bi-box-arrow-in-right me-2"></i>Login
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-outline-primary px-6 rounded-pill"
                  >
                    Create Account
                  </Link>
                </div>
                <p className="mt-4 text-secondary small">
                  Don't have an account?{" "}
                  <Link to="/register" className="text-primary fw-semibold">
                    Sign up free
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ── Loading ────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <Top />
        <section className="py-8 bg-white">
          <div className="container text-center py-5">
            <div
              className="spinner-border text-success mb-3"
              style={{ width: 48, height: 48 }}
              role="status"
            />
          </div>
        </section>
        <Footer />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ── ✅ Logged in — render enrolled courses ─────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Top />

      <section>
        <div className="container mb-8">
          <div className="row pt-4">
            <div className="col-12 d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div>
                <span className="badge bg-dark-soft rounded-pill fs-5 px-4">
                  My <span className="fw-bold">Enrolled Courses</span>
                </span>
                <p className="m-0 fs-4 lh-sm mt-1">
                  Your Enrolled Courses / Certificates
                </p>
              </div>
            </div>

            <div className="row g-2 mt-2">
              {enrolledCourses.length === 0 ? (
                <div className="col-12 text-center py-7">
                  <div
                    style={{
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      background: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <i
                      className="bi bi-journal-x"
                      style={{ fontSize: 38, color: "#aaa" }}
                    ></i>
                  </div>
                  <h4 className="text-dark fw-semibold mb-2">No Courses Yet</h4>
                  <p className="text-muted fs-5 mb-4">
                    You haven't enrolled in any courses yet. Browse our catalog
                    to get started.
                  </p>
                  <Link to="/" className="btn btn-primary px-6 rounded-pill">
                    <i className="bi bi-search me-2"></i>Browse Courses
                  </Link>
                </div>
              ) : (
                enrolledCourses.map((item, index) => {
                  const course = item.course || {};
                  const instructor = course.instructors?.[0] || {};

                  return (
                    <div
                      className="col-lg-4 col-md-6 col-12"
                      key={item._id || index}
                    >
                      <div className="col-12 px-4 d-flex align-items-center border pb-4 pt-1 border-1 shadow rounded-5 h-100">
                        <div className="d-flex flex-column px-2 mb-2 w-100">
                          {/* Purchase verified badge */}
                          <div className="d-flex align-items-center gap-1 mt-3 mb-1">
                            <i
                              className="bi bi-patch-check-fill text-success"
                              style={{ fontSize: 13 }}
                            ></i>
                            <span className="text-success small fw-medium">
                              Purchase Verified
                            </span>
                          </div>

                          <div className="d-flex gap-3 mt-1">
                            <div className="w-100">
                              <h3 className="mb-2 lh-1">
                                <Link
                                  to={`/explore-course/${item.courseId}`}
                                  className="fs-4 text-dark"
                                >
                                  {course.title || item.courseId}
                                </Link>
                              </h3>

                              <p className="mb-2 mt-1 lh-sm text-secondary">
                                {course.description?.slice(0, 80)}...
                              </p>

                              <span className="text-gray-500">
                                By {instructor.name || "Instructor"}
                              </span>

                              {instructor.rating && (
                                <div className="lh-1 mt-1">
                                  ⭐ {instructor.rating}
                                </div>
                              )}

                              {/* Payment info */}
                              {item.createdAt && (
                                <div className="mt-2">
                                  <span className="badge bg-light text-secondary rounded-pill px-3 py-1 small">
                                    <i className="bi bi-calendar3 me-1"></i>
                                    Enrolled{" "}
                                    {new Date(
                                      item.createdAt,
                                    ).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mt-4 d-flex flex-column align-items-start gap-1">
                            <Link
                              to={`/explore-course/${item.courseId}`}
                              className="btn bg-warning text-white px-4 fw-normal py-1 rounded-pill w-100 text-center"
                            >
                              <i className="bi bi-play-circle me-2"></i>Continue
                              Learning
                            </Link>
                            <button className="btn bg-success text-white px-4 fw-normal py-1 rounded-pill w-100">
                              <i className="bi bi-award me-2"></i>Download
                              Certificate
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default EnrolledCourses;
